import { useEffect, useRef, useState, type KeyboardEvent } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Popover } from "../../components/ui";
import { useVoiceInput } from "../../lib/useVoiceInput";

/**
 * Beyond Sure — chat composer (desktop + mobile) — PORTED FROM
 * `assets/form.html` so EVERY option the founder built ships in
 * the new clean shell.
 *
 * What's in (all 8 options the old composer carried, plus voice input):
 *   1. Attach `+`        — file picker (PDF / image)
 *   2. Analyse chip       — quick prompt: complete portfolio analysis
 *   3. Compare chip       — quick prompt: best 3 plans, table
 *   4. Claim help chip    — quick prompt: cashless claim walkthrough
 *   5. ₹ credits widget   — current credit balance (click → top up)
 *   6. Length selector    — Auto · S · M · H · Max (≈300/500/1000/2000 words)
 *   7. Think toggle       — Adaptive Thinking before answering
 *   8. Agent button       — guided action workflow (claim, renewal, …)
 *   9. Mic 🎙              — voice input (Web Speech API, same hook as the
 *      standalone Voice console) — speaks directly into the message box
 *      instead of a separate page. Read-aloud for the bot's replies already
 *      lives per-message (MessageBubble's "Listen" — BD3.1); this is the
 *      input half, inline where the user is already typing.
 *
 * Layout strategy (the old composer's worst defect was clipping; we
 * fix that with a structured control row):
 *   • Desktop: full control row beneath the input — left group =
 *     attach + 3 action chips, right group = credits + length +
 *     think + agent + send. Nothing wraps; the chips truncate gracefully.
 *   • Phone (< sm): only attach + length selector + send stay
 *     primary; the rest fold into a clean `•••` overflow menu so
 *     nothing is ever clipped.
 *
 * Brand fidelity: same calm pill, brand-deep send, brand-soft hover
 * — nothing from the old cramped chrome bleeds through.
 *
 * // TODO: wire to /api
 *   - onAttach / triggerUpload  → POST /api/chat/upload
 *   - credits widget click       → open Settings → Billing (or /upgrade)
 *   - length mode + Think toggle → POST /api/chat/message body
 *   - Agent button               → opens guided-workflow menu
 */

export type OutputMode = "auto" | "small" | "medium" | "high" | "max";

interface ChatComposerProps {
  onSend: (text: string) => void;
  onAttach?: (file: File) => void;
  /** True while a reply is streaming. */
  busy?: boolean;
  placeholder?: string;
  /** Optional injected quick-prompt prefill (Analyse / Compare / Claim help). */
  onQuickPrompt?: (text: string) => void;
  /** Credit balance shown in the ₹ widget. Defaults to ₹10.00 (mock). */
  creditBalance?: string;
  /** Output-mode value (controlled). Defaults to "auto". */
  outputMode?: OutputMode;
  onOutputModeChange?: (m: OutputMode) => void;
  /** "Think" toggle (controlled). */
  thinkEnabled?: boolean;
  onThinkToggle?: (next: boolean) => void;
  /** Agent button click (opens guided workflow menu). */
  onOpenAgent?: () => void;
  /** Credits widget click (opens billing / top-up). */
  onOpenCredits?: () => void;
  /** Opens the live, continuous voice-conversation overlay. Renders a
   * dedicated button next to the mic only when provided — callers that
   * don't wire this up (OcrAskAiPanel, OcrProScreen) are unaffected. */
  onOpenVoiceConversation?: () => void;
}

const MAX_ROWS = 6;

/** The three quick-prompt chips ported verbatim from form.html:9993-10004. */
const QUICK_PROMPTS = {
  analyse:
    "Give me a complete analysis of my insurance portfolio — coverage gaps, premium efficiency, and top 3 improvements.",
  compare:
    "Compare the best 3 plans for my needs. Include premium, claim ratio, key benefits, and top exclusions in a table.",
  claim:
    "How do I file a cashless insurance claim? Walk me through the exact step-by-step process with documents required.",
} as const;

const LENGTH_TIPS: Record<OutputMode, string> = {
  auto: "Auto — AI picks the best length",
  small: "Small — focused, ~300 words",
  medium: "Medium — balanced, ~500 words",
  high: "High — detailed, ~1,000 words",
  max: "Max — comprehensive, ~2,000 words",
};

export function ChatComposer({
  onSend,
  onAttach,
  busy = false,
  placeholder = "Message Beyond Sure…",
  onQuickPrompt,
  creditBalance = "10.00",
  outputMode: outputModeProp,
  onOutputModeChange,
  thinkEnabled: thinkEnabledProp,
  onThinkToggle,
  onOpenAgent,
  onOpenCredits,
  onOpenVoiceConversation,
}: ChatComposerProps) {
  const taRef = useRef<HTMLTextAreaElement | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [value, setValue] = useState("");
  const [overflowOpen, setOverflowOpen] = useState(false);
  const stt = useVoiceInput();

  // Uncontrolled fallbacks for the length + think states — so the
  // composer is usable without ANY parent state wiring. Parents that
  // care can lift state via the on*Change props.
  const [internalMode, setInternalMode] = useState<OutputMode>("auto");
  const [internalThink, setInternalThink] = useState(false);
  const outputMode = outputModeProp ?? internalMode;
  const thinkEnabled = thinkEnabledProp ?? internalThink;
  const setOutputMode = (m: OutputMode) => {
    if (onOutputModeChange) onOutputModeChange(m);
    else setInternalMode(m);
  };
  const setThink = (next: boolean) => {
    if (onThinkToggle) onThinkToggle(next);
    else setInternalThink(next);
  };

  // Auto-grow up to MAX_ROWS, then scroll.
  useEffect(() => {
    const el = taRef.current;
    if (!el) return;
    el.style.height = "auto";
    const lineHeight = 22; // matches text-[15px] leading-[1.5]
    const max = lineHeight * MAX_ROWS + 8;
    el.style.height = `${Math.min(el.scrollHeight, max)}px`;
  }, [value]);

  function submit() {
    const trimmed = value.trim();
    if (!trimmed || busy) return;
    onSend(trimmed);
    setValue("");
    requestAnimationFrame(() => {
      if (taRef.current) taRef.current.style.height = "auto";
    });
  }

  function onKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  }

  function onPickFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file || !onAttach) return;
    onAttach(file);
    e.target.value = "";
  }

  /** Fire a quick prompt — either via the callback or directly send. */
  function fireQuickPrompt(text: string) {
    if (onQuickPrompt) onQuickPrompt(text);
    else onSend(text);
  }

  const canSend = value.trim().length > 0 && !busy;

  return (
    <div
      className={[
        "w-full px-3 sm:px-5",
        "pb-[max(12px,env(safe-area-inset-bottom))]",
      ].join(" ")}
      data-testid="chat-composer"
    >
      <div
        className={[
          "mx-auto max-w-[760px]",
          "rounded-[24px]",
          "bg-[rgb(255_255_255_/_0.72)] backdrop-blur-xl",
          "border border-[var(--color-ink-08)]",
          "shadow-[0_4px_24px_-12px_rgba(20,75,51,0.18)]",
          "focus-within:border-[var(--color-brand-deep)]",
          "transition-colors duration-[var(--duration-fast)]",
        ].join(" ")}
      >
        {/* ─── Row 1 — textarea ──────────────────────────────── */}
        <div className="flex items-end gap-2 px-3 pt-2.5 pb-1.5 sm:px-3.5">
          <textarea
            ref={taRef}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={onKeyDown}
            rows={1}
            placeholder={placeholder}
            data-testid="composer-input"
            aria-label="Message Beyond Sure"
            className={[
              "flex-1 resize-none bg-transparent outline-none",
              "text-[15px] leading-[1.5] text-[var(--color-ink)]",
              "placeholder:text-[var(--color-ink-40)]",
              "py-1",
              "[scrollbar-width:thin]",
            ].join(" ")}
          />
        </div>

        {/* ─── Row 2 — full control row ──────────────────────── */}
        <div
          className={[
            "flex items-center gap-1.5 px-2 pb-2 sm:px-2.5",
            "border-t border-[var(--color-ink-08)]/60",
            "pt-2",
          ].join(" ")}
        >
          {/* Left cluster: attach + chips */}
          <div className="flex items-center gap-1.5 min-w-0">
            <Tool
              onClick={() => fileRef.current?.click()}
              testId="composer-attach"
              title="Attach a policy document"
              ariaLabel="Attach a policy document"
              icon={
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
                  <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
              }
            />
            <input
              ref={fileRef}
              type="file"
              accept="application/pdf,image/*"
              className="sr-only"
              onChange={onPickFile}
            />
            {/* Quick-prompt chips — visible from `sm`, hidden on phone */}
            <Chip
              label="Analyse"
              onClick={() => fireQuickPrompt(QUICK_PROMPTS.analyse)}
              testId="composer-chip-analyse"
              icon={
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" aria-hidden>
                  <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="2" />
                  <path d="M21 21l-4.35-4.35" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
              }
            />
            <Chip
              label="Compare"
              onClick={() => fireQuickPrompt(QUICK_PROMPTS.compare)}
              testId="composer-chip-compare"
              icon={
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" aria-hidden>
                  <path
                    d="M18 20V10M12 20V4M6 20v-6"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              }
            />
            <Chip
              label="Claim help"
              onClick={() => fireQuickPrompt(QUICK_PROMPTS.claim)}
              testId="composer-chip-claim"
              icon={
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" aria-hidden>
                  <path
                    d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinejoin="round"
                  />
                  <path d="M14 2v6h6" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
                </svg>
              }
            />
          </div>

          {/* Right cluster: credits + length + think + agent + send */}
          <div className="ml-auto flex items-center gap-1.5">
            {/* Credits widget (desktop) — Popover surface with balance,
                top-up CTA, and a quick link to transactions. Replaces the
                old "click → navigate away" behavior. Mobile still hides
                the widget; the same info surfaces in Settings → Billing. */}
            <Popover
              side="top"
              align="end"
              content={
                <div className="w-[260px] p-4">
                  <div className="flex items-baseline gap-1.5">
                    <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[var(--color-ink-40)]">
                      Credits
                    </span>
                  </div>
                  <div className="mt-1 flex items-baseline gap-1.5">
                    <span className="text-[var(--color-brand-deep)] text-[26px] leading-none font-semibold">
                      ₹
                    </span>
                    <span className="bsure-display text-[28px] leading-none font-bold tabular-nums text-[var(--color-ink)]">
                      {creditBalance}
                    </span>
                  </div>
                  <p className="mt-2.5 text-[12px] leading-[1.45] text-[var(--color-ink-60)]">
                    Chat is unlimited. Credits cover OCR, deep analysis,
                    and agent workflows.
                  </p>
                  <button
                    type="button"
                    onClick={() => onOpenCredits?.()}
                    data-testid="composer-credits-topup"
                    className={[
                      "mt-3 w-full h-9 rounded-[10px]",
                      "bg-[var(--color-brand-deep)] text-white",
                      "text-[13px] font-semibold",
                      "hover:bg-[var(--color-brand)] transition-colors duration-[var(--duration-fast)]",
                      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-brand)] focus-visible:ring-offset-1",
                    ].join(" ")}
                  >
                    Top up credits
                  </button>
                  <button
                    type="button"
                    onClick={() => onOpenCredits?.()}
                    className={[
                      "mt-2 w-full text-[12px] text-[var(--color-ink-60)]",
                      "hover:text-[var(--color-brand-deep)] transition-colors duration-[var(--duration-fast)]",
                    ].join(" ")}
                  >
                    View transactions →
                  </button>
                </div>
              }
            >
              {({ ref, onClick, "aria-expanded": ariaExpanded, "aria-controls": ariaControls }) => (
                <button
                  ref={ref as React.Ref<HTMLButtonElement>}
                  type="button"
                  onClick={onClick}
                  aria-expanded={ariaExpanded}
                  aria-controls={ariaControls}
                  aria-haspopup="dialog"
                  data-testid="composer-credits"
                  title="Your credits"
                  className={[
                    "hidden sm:inline-flex items-center gap-1 h-7 px-2 rounded-[8px]",
                    "border border-[var(--color-ink-08)] bg-[var(--color-paper)]",
                    "text-[11px] font-semibold text-[var(--color-ink)]",
                    "hover:border-[var(--color-brand-deep)] hover:text-[var(--color-brand-deep)]",
                    "transition-colors duration-[var(--duration-fast)]",
                  ].join(" ")}
                >
                  <span className="text-[var(--color-brand-deep)]">₹</span>
                  <span className="tabular-nums" data-testid="composer-credit-bal">
                    {creditBalance}
                  </span>
                  <span className="text-[var(--color-ink-40)]">+</span>
                </button>
              )}
            </Popover>

            {/* Length selector */}
            <LengthSelector value={outputMode} onChange={setOutputMode} />

            {/* Think toggle (desktop) */}
            <button
              type="button"
              onClick={() => setThink(!thinkEnabled)}
              data-testid="composer-think"
              aria-pressed={thinkEnabled}
              title={
                thinkEnabled
                  ? "Adaptive Thinking ON — reasons before answering"
                  : "Adaptive Thinking OFF"
              }
              className={[
                "hidden sm:inline-flex items-center gap-1 h-7 px-2 rounded-[8px]",
                "text-[11px] font-semibold",
                "transition-colors duration-[var(--duration-fast)]",
                thinkEnabled
                  ? "bg-[var(--color-brand-soft)] text-[var(--color-brand-deep)] border border-[var(--color-brand-deep)]/30"
                  : "bg-[var(--color-paper)] text-[var(--color-ink-60)] border border-[var(--color-ink-08)] hover:text-[var(--color-ink)]",
              ].join(" ")}
            >
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" aria-hidden>
                <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
                <path
                  d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3M12 17h.01"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                />
              </svg>
              Think
            </button>

            {/* Agent button (desktop) */}
            <button
              type="button"
              onClick={() => onOpenAgent?.()}
              data-testid="composer-agent"
              title="Start a guided action workflow (claim, renewal, etc.)"
              className={[
                "hidden sm:inline-flex items-center gap-1 h-7 px-2 rounded-[8px]",
                "border border-[var(--color-ink-08)] bg-[var(--color-paper)]",
                "text-[11px] font-semibold text-[var(--color-ink-60)]",
                "hover:border-[var(--color-brand-deep)] hover:text-[var(--color-brand-deep)]",
                "transition-colors duration-[var(--duration-fast)]",
              ].join(" ")}
            >
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" aria-hidden>
                <path
                  d="M9 11l3 3L22 4M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Agent
            </button>

            {/* Phone overflow ••• menu */}
            <div className="sm:hidden relative">
              <button
                type="button"
                onClick={() => setOverflowOpen((o) => !o)}
                data-testid="composer-overflow"
                aria-label="More composer options"
                className={[
                  "inline-flex items-center justify-center h-8 w-8 rounded-full",
                  "text-[var(--color-ink-60)] hover:bg-[var(--color-ink-08)]",
                ].join(" ")}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
                  <circle cx="5" cy="12" r="1.6" fill="currentColor" />
                  <circle cx="12" cy="12" r="1.6" fill="currentColor" />
                  <circle cx="19" cy="12" r="1.6" fill="currentColor" />
                </svg>
              </button>
              <AnimatePresence>
                {overflowOpen ? (
                  <motion.div
                    initial={{ opacity: 0, y: 4 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 4 }}
                    transition={{ duration: 0.14 }}
                    role="menu"
                    data-testid="composer-overflow-menu"
                    className={[
                      "absolute bottom-full right-0 mb-1.5 z-30",
                      "min-w-[200px] rounded-[12px] p-1.5",
                      "bg-[var(--color-paper)] border border-[var(--color-ink-08)]",
                      "shadow-[0_10px_28px_-12px_rgba(20,75,51,0.32)]",
                    ].join(" ")}
                  >
                    <OverflowItem
                      label="Analyse"
                      onClick={() => {
                        fireQuickPrompt(QUICK_PROMPTS.analyse);
                        setOverflowOpen(false);
                      }}
                    />
                    <OverflowItem
                      label="Compare"
                      onClick={() => {
                        fireQuickPrompt(QUICK_PROMPTS.compare);
                        setOverflowOpen(false);
                      }}
                    />
                    <OverflowItem
                      label="Claim help"
                      onClick={() => {
                        fireQuickPrompt(QUICK_PROMPTS.claim);
                        setOverflowOpen(false);
                      }}
                    />
                    <div className="h-px bg-[var(--color-ink-08)] my-1" />
                    <OverflowItem
                      label={`Credits · ₹${creditBalance}`}
                      onClick={() => {
                        onOpenCredits?.();
                        setOverflowOpen(false);
                      }}
                    />
                    <OverflowItem
                      label={`Think · ${thinkEnabled ? "On" : "Off"}`}
                      onClick={() => {
                        setThink(!thinkEnabled);
                        setOverflowOpen(false);
                      }}
                    />
                    <OverflowItem
                      label="Agent workflow"
                      onClick={() => {
                        onOpenAgent?.();
                        setOverflowOpen(false);
                      }}
                    />
                  </motion.div>
                ) : null}
              </AnimatePresence>
            </div>

            {/* Mic — voice input, inline where the user is already typing.
                Visible on both mobile and desktop (unlike the other
                secondary controls) since voice is arguably more useful on
                phone. Disabled + titled honestly when the browser has no
                SpeechRecognition (Firefox, headless) — never a raw error. */}
            <motion.button
              type="button"
              onClick={() => stt.toggle(setValue)}
              disabled={stt.supported === false}
              whileTap={stt.supported === false ? undefined : { scale: 0.94 }}
              aria-pressed={stt.listening}
              aria-label={stt.listening ? "Stop voice input" : "Speak your message"}
              title={
                stt.supported === false
                  ? "Voice input not supported in this browser — try Chrome or Edge"
                  : stt.listening
                  ? "Listening… tap to stop"
                  : "Speak your message"
              }
              data-testid="composer-mic"
              className={[
                "relative shrink-0 inline-flex items-center justify-center",
                "h-8 w-8 rounded-full transition-colors duration-[var(--duration-fast)]",
                stt.supported === false
                  ? "bg-[var(--color-ink-08)] text-[var(--color-ink-40)] cursor-not-allowed"
                  : stt.listening
                  ? "bg-[var(--color-brand-deep)] text-[var(--color-paper)]"
                  : "text-[var(--color-ink-60)] hover:text-[var(--color-brand-deep)] hover:bg-[var(--color-brand-soft)]",
              ].join(" ")}
            >
              {stt.listening ? (
                <motion.span
                  className="absolute inset-0 rounded-full border-2 border-[var(--color-brand-deep)]"
                  animate={{ scale: [1, 1.35], opacity: [0.6, 0] }}
                  transition={{ duration: 1.4, repeat: Infinity, ease: "easeOut" }}
                  aria-hidden
                />
              ) : null}
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden>
                <rect x="9" y="2" width="6" height="12" rx="3" stroke="currentColor" strokeWidth="2" />
                <path
                  d="M5 11a7 7 0 0 0 14 0M12 18v4"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </motion.button>

            {/* Voice conversation — live, continuous, interruptible voice mode
                (VAD-driven turn-taking, not tap-to-record-once). Separate from
                the mic above so the existing single-utterance flow is
                untouched; see docs/VOICE-5-conversation-mode.md. */}
            {onOpenVoiceConversation ? (
              <motion.button
                type="button"
                onClick={onOpenVoiceConversation}
                whileTap={{ scale: 0.94 }}
                aria-label="Start voice conversation"
                title="Start a live voice conversation"
                data-testid="composer-voice-conversation"
                className="shrink-0 inline-flex items-center justify-center h-8 w-8 rounded-full text-[var(--color-ink-60)] hover:text-[var(--color-brand-deep)] hover:bg-[var(--color-brand-soft)] transition-colors duration-[var(--duration-fast)]"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
                  <path
                    d="M4 4.5c0-.83.67-1.5 1.5-1.5H8a1 1 0 0 1 1 .8l.9 4.2a1 1 0 0 1-.5 1.08L7.7 9.9a12 12 0 0 0 6.4 6.4l1.82-1.7a1 1 0 0 1 1.08-.5l4.2.9a1 1 0 0 1 .8 1v2.5c0 .83-.67 1.5-1.5 1.5C10.5 20 4 13.5 4 5.5"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinejoin="round"
                  />
                </svg>
              </motion.button>
            ) : null}

            {/* Send */}
            <motion.button
              type="button"
              onClick={submit}
              disabled={!canSend}
              whileTap={canSend ? { scale: 0.94 } : undefined}
              data-testid="composer-send"
              aria-label={busy ? "Replying…" : "Send"}
              className={[
                "shrink-0 inline-flex items-center justify-center",
                "h-8 w-8 rounded-full",
                canSend
                  ? "bg-[var(--color-brand-deep)] text-[var(--color-paper)] shadow-[0_2px_10px_-4px_rgba(20,75,51,0.55)]"
                  : "bg-[var(--color-ink-08)] text-[var(--color-ink-40)] cursor-not-allowed",
                "transition-colors duration-[var(--duration-fast)]",
              ].join(" ")}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
                <path
                  d="M12 19V5M5 12l7-7 7 7"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </motion.button>
          </div>
        </div>
      </div>

      <p
        className={[
          "mx-auto max-w-[760px] mt-1.5 px-2",
          "hidden sm:block text-center text-[11.5px] text-[var(--color-ink-40)]",
        ].join(" ")}
      >
        Enter to send · Shift + Enter for a new line · Attach a policy to fact-check it
      </p>
    </div>
  );
}

/* ───────────────────────────────────────────────────────────
 * LengthSelector — Auto/S/M/H/Max segmented control
 * ─────────────────────────────────────────────────────────── */

function LengthSelector({
  value,
  onChange,
}: {
  value: OutputMode;
  onChange: (m: OutputMode) => void;
}) {
  const items: OutputMode[] = ["auto", "small", "medium", "high", "max"];
  const labels: Record<OutputMode, string> = {
    auto: "Auto",
    small: "S",
    medium: "M",
    high: "H",
    max: "Max",
  };
  return (
    <div
      className={[
        "inline-flex items-center h-7 rounded-[8px] p-0.5",
        "border border-[var(--color-ink-08)] bg-[var(--color-paper)]",
      ].join(" ")}
      role="group"
      aria-label="Output length"
      data-testid="composer-length"
    >
      {items.map((m) => {
        const on = value === m;
        return (
          <button
            key={m}
            type="button"
            onClick={() => onChange(m)}
            title={LENGTH_TIPS[m]}
            data-testid={`composer-length-${m}`}
            data-active={on}
            className={[
              "px-1.5 h-6 rounded-[6px] text-[11px] font-semibold",
              "transition-colors duration-[var(--duration-fast)]",
              on
                ? "bg-[var(--color-brand-deep)] text-[var(--color-paper)]"
                : "text-[var(--color-ink-60)] hover:text-[var(--color-ink)]",
            ].join(" ")}
          >
            {labels[m]}
          </button>
        );
      })}
    </div>
  );
}

/* ───────────────────────────────────────────────────────────
 * Quick-prompt chip — left-cluster chips
 * ─────────────────────────────────────────────────────────── */

function Chip({
  label,
  onClick,
  icon,
  testId,
}: {
  label: string;
  onClick: () => void;
  icon: React.ReactNode;
  testId: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={[
        "hidden sm:inline-flex items-center gap-1 h-7 px-2 rounded-full",
        "border border-[var(--color-ink-08)] bg-[var(--color-paper)]",
        "text-[11.5px] font-medium text-[var(--color-ink-60)]",
        "hover:border-[var(--color-brand-deep)] hover:text-[var(--color-brand-deep)] hover:bg-[var(--color-brand-soft)]",
        "transition-colors duration-[var(--duration-fast)]",
      ].join(" ")}
    >
      {icon}
      <span>{label}</span>
    </button>
  );
}

/* ───────────────────────────────────────────────────────────
 * Tool — circular icon-only control (attach button)
 * ─────────────────────────────────────────────────────────── */

function Tool({
  onClick,
  icon,
  title,
  ariaLabel,
  testId,
}: {
  onClick: () => void;
  icon: React.ReactNode;
  title: string;
  ariaLabel: string;
  testId: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      title={title}
      aria-label={ariaLabel}
      className={[
        "shrink-0 inline-flex items-center justify-center",
        "h-7 w-7 rounded-full",
        "text-[var(--color-ink-60)] hover:text-[var(--color-brand-deep)]",
        "hover:bg-[var(--color-brand-soft)]",
        "transition-colors duration-[var(--duration-fast)]",
      ].join(" ")}
    >
      {icon}
    </button>
  );
}

/* ───────────────────────────────────────────────────────────
 * OverflowItem — mobile ••• menu row
 * ─────────────────────────────────────────────────────────── */

function OverflowItem({
  label,
  onClick,
}: {
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      className={[
        "w-full text-left px-2.5 py-1.5 rounded-[8px]",
        "text-[13px] text-[var(--color-ink)]",
        "hover:bg-[var(--color-brand-soft)]",
        "transition-colors duration-[var(--duration-fast)]",
      ].join(" ")}
    >
      {label}
    </button>
  );
}

export default ChatComposer;
